import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    ArrowRight,
    Briefcase,
    HardHat,
    Zap,
    Sparkles,
    Wrench,
    Truck,
    Leaf,
    CheckCircle2,
    Share2,
} from "lucide-react";
import { API } from "../lib/api";
import { buildJobPostingLd } from "../lib/jobPostingLd";
import axios from "axios";

const ICON_MAP = {
    briefcase: Briefcase,
    hardhat: HardHat,
    zap: Zap,
    sparkles: Sparkles,
    wrench: Wrench,
    truck: Truck,
    leaf: Leaf,
};

function scrollToApply(position) {
    const el = document.getElementById("bewerbung");
    if (el) {
        window.scrollTo({ top: el.offsetTop - 72, behavior: "smooth" });
        window.dispatchEvent(new CustomEvent("moebus:setPosition", { detail: position }));
    }
}

async function shareJob(job) {
    const url = `${window.location.origin}/api/share/${job.slug}`;
    const title = `${job.title} ${job.suffix || ""} – Möbus Umweltschutz GmbH`;
    if (navigator.share) {
        try {
            await navigator.share({ title, url });
            return;
        } catch {
            return;
        }
    }
    try {
        await navigator.clipboard.writeText(url);
        toast.success("Link zur Stelle kopiert – jetzt einfach teilen!");
    } catch {
        toast.error("Link konnte nicht kopiert werden.");
    }
}

function TiltCard({ children, testid }) {
    const ref = useRef(null);

    const onMove = (e) => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width;
        const py = (e.clientY - r.top) / r.height;
        const rx = (py - 0.5) * -8;
        const ry = (px - 0.5) * 8;
        el.style.transform = `perspective(1000px) rotateX(${rx}deg) rotateY(${ry}deg) translateZ(0)`;
    };
    const onLeave = () => {
        const el = ref.current;
        if (el) el.style.transform = "perspective(1000px) rotateX(0) rotateY(0) translateZ(0)";
    };

    return (
        <div
            ref={ref}
            data-testid={testid}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            className="tilt-card flex flex-col bg-white border border-moebus-border rounded-lg p-7 md:p-8 hover:shadow-2xl transition-shadow duration-300 group h-full"
        >
            <div className="tilt-card-inner flex flex-col h-full">{children}</div>
        </div>
    );
}

export default function JobCards() {
    const [jobs, setJobs] = useState([]);
    const [intro, setIntro] = useState({
        headline: "Menschen, die mitdenken, anpacken und mit uns wachsen.",
        paragraphs: [
            "Ob im Büro, im Marketing, in der Organisation, auf der Baustelle oder in der Technik – bei Möbus Umweltschutz suchen wir Menschen, die Verantwortung übernehmen, zuverlässig arbeiten und Lust auf abwechslungsreiche Aufgaben haben.",
            "Wähle den Bereich aus, der zu dir passt, und bewirb dich direkt online.",
        ],
    });

    useEffect(() => {
        axios
            .get(`${API}/content/jobsintro`)
            .then((r) => {
                if (r.data?.headline) setIntro(r.data);
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        axios
            .get(`${API}/jobs`)
            .then(({ data }) => setJobs(data.items || []))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!jobs.length) return;
        const postings = jobs
            .filter((j) => j.slug !== "initiativbewerbung")
            .map((j) => buildJobPostingLd(j));
        let el = document.getElementById("jobs-jsonld");
        if (!el) {
            el = document.createElement("script");
            el.type = "application/ld+json";
            el.id = "jobs-jsonld";
            document.head.appendChild(el);
        }
        el.textContent = JSON.stringify(postings);
    }, [jobs]);

    useEffect(() => {
        if (!jobs.length) return;
        const params = new URLSearchParams(window.location.search);
        const slug = params.get("stelle");
        if (slug) {
            const job = jobs.find((j) => j.slug === slug);
            if (job) {
                window.dispatchEvent(new CustomEvent("moebus:setPosition", { detail: job.position }));
                if (params.get("bewerben")) {
                    setTimeout(() => {
                        const el = document.getElementById("bewerbung");
                        if (el) window.scrollTo({ top: el.offsetTop - 72, behavior: "smooth" });
                    }, 700);
                } else {
                    setTimeout(() => {
                        const el = document.getElementById(`job-${job.slug}`);
                        if (el) {
                            window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 84, behavior: "smooth" });
                        }
                    }, 900);
                }
            }
        }
    }, [jobs]);

    return (
        <section
            id="stellen"
            data-testid="jobs-section"
            className="py-20 md:py-28 bg-white relative overflow-hidden"
        >
            <div className="absolute -top-32 right-1/4 w-[500px] h-[500px] bg-emerald-100 rounded-full blur-3xl opacity-50 pointer-events-none"></div>

            <div className="relative container mx-auto px-4 md:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.7 }}
                    className="max-w-3xl mb-14 md:mb-20"
                >
                    <div className="text-xs tracking-[0.3em] uppercase text-moebus-green font-semibold mb-4">
                        Was wir suchen
                    </div>
                    <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-moebus-deep leading-tight text-balance">
                        {intro.headline}
                    </h2>
                    <div className="mt-6 space-y-4 text-base md:text-lg text-slate-600 leading-relaxed">
                        {(intro.paragraphs || []).map((p, i) => (
                            <p key={i}>{p}</p>
                        ))}
                    </div>
                </motion.div>

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-80px" }}
                    variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.15 } } }}
                    className="grid md:grid-cols-2 gap-6 lg:gap-8"
                >
                    {jobs.map((job) => {
                        const Icon = ICON_MAP[job.icon] || Briefcase;
                        return (
                            <motion.article
                                key={job.id}
                                id={`job-${job.slug}`}
                                variants={{
                                    hidden: { opacity: 0, y: 40 },
                                    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
                                }}
                            >
                                <TiltCard testid={`job-card-${job.position}`}>
                                    <div className="flex items-start justify-between mb-6 gap-3">
                                        <div className="w-12 h-12 rounded-md bg-moebus-deep text-white flex items-center justify-center group-hover:bg-moebus-green transition-colors flex-shrink-0">
                                            <Icon size={22} strokeWidth={1.8} />
                                        </div>
                                        <div className="flex items-center gap-3 pt-1">
                                            <span className="text-xs uppercase tracking-widest text-slate-400 font-medium">
                                                {job.suffix}
                                            </span>
                                            <button
                                                data-testid={`share-${job.position}`}
                                                onClick={() => shareJob(job)}
                                                title="Stelle teilen"
                                                className="text-slate-400 hover:text-moebus-green transition-colors"
                                            >
                                                <Share2 size={18} />
                                            </button>
                                        </div>
                                    </div>

                                    <h3 className="font-heading font-bold text-xl md:text-2xl text-moebus-deep leading-tight">
                                        {job.title}
                                    </h3>
                                    <p className="mt-4 text-sm md:text-base text-slate-600 leading-relaxed">
                                        {job.description}
                                    </p>

                                    {job.tasks?.length > 0 && (
                                        <div className="mt-6">
                                            <div className="text-xs font-semibold uppercase tracking-widest text-moebus-deep mb-3">
                                                Aufgaben
                                            </div>
                                            <ul className="space-y-2">
                                                {job.tasks.map((t) => (
                                                    <li key={t} className="flex gap-2 text-sm text-slate-600">
                                                        <CheckCircle2
                                                            size={16}
                                                            className="text-moebus-green flex-shrink-0 mt-0.5"
                                                        />
                                                        <span>{t}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {job.profile?.length > 0 && (
                                        <div className="mt-6">
                                            <div className="text-xs font-semibold uppercase tracking-widest text-moebus-deep mb-3">
                                                Dein Profil
                                            </div>
                                            <ul className="space-y-2">
                                                {job.profile.map((t) => (
                                                    <li key={t} className="flex gap-2 text-sm text-slate-600">
                                                        <CheckCircle2
                                                            size={16}
                                                            className="text-moebus-blue flex-shrink-0 mt-0.5"
                                                        />
                                                        <span>{t}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    <div className="flex-1"></div>

                                    <button
                                        data-testid={`apply-${job.position}`}
                                        onClick={() => scrollToApply(job.position)}
                                        className="mt-8 inline-flex items-center justify-center gap-2 bg-moebus-deep hover:bg-moebus-green text-white font-medium px-5 py-3 rounded-md transition-all w-full group/btn relative overflow-hidden"
                                    >
                                        <span className="relative z-10 flex items-center gap-2">
                                            Auf diese Stelle bewerben
                                            <ArrowRight
                                                size={16}
                                                className="group-hover/btn:translate-x-1 transition-transform"
                                            />
                                        </span>
                                    </button>
                                </TiltCard>
                            </motion.article>
                        );
                    })}
                </motion.div>
            </div>
        </section>
    );
}
